// Reading a run file for the import (task eval_admin_runs; the endpoint is
// eval_run_store's POST /v1/admin/evals/runs/import, ADR 0232 §12). The
// browser checks only what it can before sending: the size the route reads
// and that the file is JSON. Everything about the run itself is the server's.
// It sends the file gzipped (TOM-305): a full long run's file is larger than
// the route's body limit, and JSON compresses well. A browser without
// CompressionStream sends the JSON as before, under the body limit.
// Pure apart from File.text() and CompressionStream: tested in upload.test.ts.

/** The route's body limit (src/api/admin/evals.ts EVAL_IMPORT_BODY_LIMIT): 32 MiB, as sent. */
export const EVAL_IMPORT_MAX_BYTES = 32 * 1024 * 1024;

/**
 * The largest file a gzipped upload may inflate to (src/api/admin/evals.ts
 * EVAL_IMPORT_INFLATED_LIMIT): 96 MiB.
 */
export const EVAL_IMPORT_MAX_INFLATED_BYTES = 96 * 1024 * 1024;

/** What the import is sent: the file gzipped, or its JSON as it was parsed. */
export type RunFileUpload =
  | { encoding: 'gzip'; data: Blob }
  | { encoding: 'identity'; body: unknown };

export type RunFileRead = { ok: true; upload: RunFileUpload } | { ok: false; message: string };

/** The part of a File the reader uses, so tests can pass a plain object. */
export interface RunFileLike {
  name: string;
  size: number;
  text(): Promise<string>;
}

/** Whether this browser can gzip the upload (every current browser can). */
export function canGzip(): boolean {
  return typeof CompressionStream === 'function';
}

/** The text gzipped, as the route inflates it. */
export async function gzipText(text: string): Promise<Blob> {
  const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'));
  return new Response(stream).blob();
}

const MiB = 1024 * 1024;

export async function readRunFile(file: RunFileLike, gzip = canGzip()): Promise<RunFileRead> {
  if (file.size === 0) return { ok: false, message: `${file.name} is empty.` };
  const limit = gzip ? EVAL_IMPORT_MAX_INFLATED_BYTES : EVAL_IMPORT_MAX_BYTES;
  if (file.size > limit) {
    return {
      ok: false,
      message: `${file.name} is larger than the ${limit / MiB} MiB an import accepts.`,
    };
  }
  let text: string;
  try {
    text = await file.text();
  } catch {
    return { ok: false, message: `${file.name} could not be read.` };
  }
  let body: unknown;
  try {
    body = JSON.parse(text) as unknown;
  } catch {
    return { ok: false, message: `${file.name} is not JSON. Upload the file --out wrote.` };
  }
  if (!gzip) return { ok: true, upload: { encoding: 'identity', body } };
  const data = await gzipText(text);
  if (data.size > EVAL_IMPORT_MAX_BYTES) {
    return {
      ok: false,
      message: `${file.name} is larger than the ${EVAL_IMPORT_MAX_BYTES / MiB} MiB an import accepts, even gzipped.`,
    };
  }
  return { ok: true, upload: { encoding: 'gzip', data } };
}

/** What POST /v1/admin/evals/runs/import answers. */
export interface EvalImportOutcome {
  runId: string;
  imported: boolean;
  superseded: boolean;
}

/** The sentence the upload shows once the server has answered. */
export function importOutcomeText(o: EvalImportOutcome): string {
  if (!o.imported) return 'This run file is already stored.';
  if (o.superseded) return 'Stored. It replaces an earlier import of the same run.';
  return 'Stored.';
}

const IMPORT_ERRORS: Record<string, string> = {
  unsupported_schema_version: 'The file is from a run file version this server cannot read.',
  invalid_run_file: 'The file is not a run file the server can read.',
  foreign_boat: 'The file holds a moment from a boat this server does not have.',
  run_id_conflict: 'A different run with the same id is already stored.',
  result_id_conflict: 'A result id in the file is already stored under another run.',
  audit_unavailable: 'The import could not be audited, so nothing was stored.',
  payload_too_large: 'The file is larger than an import accepts.',
  invalid_gzip: 'The upload was not sent as gzip the server can read.',
};

/** The message for a refused import: the server's code in words, with its own detail. */
export function importErrorText(error: string | undefined, message: string | undefined): string {
  const known = error !== undefined ? IMPORT_ERRORS[error] : undefined;
  if (known !== undefined && message !== undefined && message.length > 0) {
    return `${known} (${message})`;
  }
  return known ?? message ?? 'The import failed.';
}
